import _ from 'underscore';
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { ImageModelPropTypes } from '@bingads-webui-campaign/image-experience-proptypes';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';

import {
  AdsLabel,
  BoostDiscover,
  BoostWindows,
  BusinessNameWithAds,
  CallToAction,
  ContactDecorator,
  Description,
  FinalUrl,
  LongHeadline,
  PersonaDecorator,
  ProngDark,
  ProngLight,
  ShortHeadline,
  Win11StartDark,
  Win11StartLight,
  Win10StartDark,
  Win10StartLight,
  CroppedImageView,
  WinStore,
} from './decorators/index';
import { getPlacementCardStyles } from './styles/ad-placement-card-preview';

export const AdPlacementCardPropTypes = {
  propTypes: {
    // data props
    key: PropTypes.string,
    className: PropTypes.string,
    hotspots: PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.string,
      desc: PropTypes.string,
      actionText: PropTypes.string,
      uri: PropTypes.string,
    })),
    image: ImageModelPropTypes,
    logo: ImageModelPropTypes,
    previewTexts: PropTypes.shape({
      shortHeadline: PropTypes.string,
      longHeadline: PropTypes.string,
      description: PropTypes.string,
      businessName: PropTypes.string,
      url: PropTypes.string,
      callToAction: PropTypes.string,
      displayUrl: PropTypes.string,
    }).isRequired,
    decoratorsData: PropTypes.shape({}),
    styleOverrides: PropTypes.shape({}),

    // rendering props
    aspectRatio: PropTypes.number,
    dismissText: PropTypes.string,
    imagePreviewHeight: PropTypes.number,
    logoHeight: PropTypes.number,
    previewTypeStringKey: PropTypes.string.isRequired,
    disclaimerMessage: PropTypes.string,

    // toggle props
    showAction: PropTypes.bool,
    showAdditionalDisclaimer: PropTypes.bool,
    showAdsLabel: PropTypes.bool,
    showAdsLabelInlineWithDescription: PropTypes.bool,
    showAdsLabelWithBusinessName: PropTypes.bool,
    showBoostDiscover: PropTypes.bool,
    showBoostWindows: PropTypes.bool,
    showBusinessNameFirst: PropTypes.bool,
    showBusinessName: PropTypes.bool,
    showDescription: PropTypes.bool,
    showGradient: PropTypes.bool,
    showHotspots: PropTypes.bool,
    showImage: PropTypes.bool,
    showImageGradient: PropTypes.bool,
    showLogo: PropTypes.bool,
    showLongHeadline: PropTypes.bool,
    showMultipleDescription: PropTypes.bool,
    showMultipleLineLongHeadline: PropTypes.bool,
    showPreHeadlineDivider: PropTypes.bool,
    showPreviewTypeString: PropTypes.bool,
    showProngDark: PropTypes.bool,
    showProngLight: PropTypes.bool,
    showPostHeadlineDivider: PropTypes.bool,
    showShortHeadline: PropTypes.bool,
    showShortHeadlineMultiline: PropTypes.bool,
    showWin11StartLight: PropTypes.bool,
    showWin11StartDark: PropTypes.bool,
    showWin10StartLight: PropTypes.bool,
    showWin10StartDark: PropTypes.bool,
    showUrl: PropTypes.bool,
    showUrlMultiline: PropTypes.bool,
    showPersonaDecorator: PropTypes.bool,
    showPhoneAndAddressDecorator: PropTypes.bool,
    showWinStore: PropTypes.bool,
  },
  defaultProps: {
    key: '',
    className: '',
    image: null,
    hotspots: [],
    logo: null,
    decoratorsData: {},

    dismissText: '',
    logoHeight: 24,
    aspectRatio: 1,
    imagePreviewHeight: 150,
    disclaimerMessage: '',

    showAction: false,
    showAdditionalDisclaimer: false,
    showAdsLabel: false,
    showAdsLabelInlineWithDescription: false,
    showAdsLabelWithBusinessName: false,
    showBoostDiscover: false,
    showBoostWindows: false,
    showBusinessNameFirst: false,
    showBusinessName: false,
    showDescription: false,
    showGradient: false,
    showHotspots: false,
    showImage: false,
    showImageGradient: false,
    showLogo: false,
    showLongHeadline: false,
    showMultipleDescription: false,
    showMultipleLineLongHeadline: false,
    showPreHeadlineDivider: false,
    showPreviewTypeString: true,
    showProng: false,
    showPostHeadlineDivider: false,
    showShortHeadline: false,
    showShortHeadlineMultiline: false,
    showWin11StartLight: false,
    showWin11StartDark: false,
    showWin10StartLight: false,
    showWin10StartDark: false,
    showUrl: false,
    showUrlMultiline: false,
    showWinStore: false,
  },
};

class UnstyledAdPlacementCardPreview extends React.PureComponent {
  constructor(props, context) {
    super(props, context);

    this.multiLineTextRefs = [
      React.createRef(),
      React.createRef(),
      React.createRef(),
    ];
  }

  componentDidUpdate() {
    this.multiLineTextRefs.forEach((ref) => {
      if (ref.current) {
        ref.current.processText();
      }
    });
  }

  render() {
    const {
      showAdditionalDisclaimer,
      showAdsLabelWithBusinessName,
      showShortHeadlineMultiline,
      showMultipleLineLongHeadline,
      showUrlMultiline,
      showImage,
      disclaimerMessage,
      dismissText,
      showHotspots,
      hotspots,
      previewTexts,
      decoratorsData,
      logo,
      showLogo,
      showMultipleDescription,
      classes,
      showBoostDiscover,
      showBoostWindows,
      showGradient,
      showImageGradient,
      showPreviewTypeString,
      previewTypeStringKey,
      styleOverrides,
    } = this.props;
    const srcForCroppedImageView = showImage && this.props.image && this.props.image.FinalSrc;

    return (
      <div className="ad-placement-card-preview">
        <a
          className={classnames('responsive-ad-preview-content', classes.previewContent)}
          target="_blank"
          rel="noopener noreferrer"
          href={previewTexts.url}
        >
          {
            this.props.showProngLight &&
            <ProngLight
              styleOverrides={styleOverrides}
            />
          }
          {
            this.props.showProngDark &&
            <ProngDark
              styleOverrides={styleOverrides}
            />
          }
          {
            this.props.showWin11StartLight &&
            <Win11StartLight
              styleOverrides={styleOverrides}
            />
          }
          {
            this.props.showWin11StartDark &&
            <Win11StartDark
              styleOverrides={styleOverrides}
            />
          }
          {
            this.props.showWin10StartLight &&
            <Win10StartLight
              styleOverrides={styleOverrides}
            />
          }
          {
            this.props.showWin10StartDark &&
            <Win10StartDark
              styleOverrides={styleOverrides}
            />
          }
          {
            this.props.showWinStore &&
            <WinStore
              styleOverrides={styleOverrides}
            />
          }
          {
            this.props.showPersonaDecorator &&
            <PersonaDecorator
              data={
                _.defaults(decoratorsData.persona, {
                  sponsoredText: this.props.i18n.getString(_TL_('Sponsored')),
                })
              }
              styleOverrides={styleOverrides}
            />
          }
          <div className={classnames('image-container', classes.imageContainer, showGradient ? classes.gradient : '')}>
            {
                this.props.showAdsLabel &&
                <AdsLabel
                  adsLabelText={this.props.i18n.getString(_TL_('Ad'))}
                  styleOverrides={styleOverrides}
                />
            }
            {
              showImageGradient &&
              <div className={classnames('gradient', classes.imageGradient)} />
            }
            {
              showImage && this.props.image &&
              <CroppedImageView
                resizeImageHeight={this.props.imagePreviewHeight}
                src={srcForCroppedImageView}
                originalWidth={this.props.image.Width}
                originalHeight={this.props.image.Height}
                SourceX={this.props.image.CropSettings.SourceX}
                SourceY={this.props.image.CropSettings.SourceY}
                SourceWidth={this.props.image.CropSettings.SourceWidth}
                SourceHeight={this.props.image.CropSettings.SourceHeight}
                aspectRatio={this.props.aspectRatio}
              />
            }
          </div>
          {
            showBoostDiscover &&
            <BoostDiscover text={this.props.i18n.getString(_TL_('Discover'))} styleOverrides={styleOverrides} />
          }
          {
            showBoostWindows &&
            <BoostWindows
              welcomeText={this.props.i18n.getString(_TL_('Welcome back. Dismiss the lock screen to sign back into Windows.'))}
              timeText={this.props.i18n.getString(_TL_('10:36'))}
              dateText={this.props.i18n.getString(_TL_('Tuesday, October 18'))}
              styleOverrides={styleOverrides}
            />
          }
          {showLogo && logo &&
            <div className="logo-container">
              <CroppedImageView
                resizeImageHeight={this.props.logoHeight}
                src={logo.Src}
                originalWidth={logo.Width}
                originalHeight={logo.Height}
                SourceX={logo.CropSettings.SourceX}
                SourceY={logo.CropSettings.SourceY}
                SourceWidth={logo.CropSettings.SourceWidth}
                SourceHeight={logo.CropSettings.SourceHeight}
                aspectRatio={1}
              />
            </div>
          }

          {
            showHotspots &&
            hotspots.map((hotspot, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <div className={`hotspot${index}`} key={`${hotspot.title}${index}`}>
                <div className="hotspot-title" >{hotspot.title}</div>
                <div className="hotspot-action" >{hotspot.actionText}</div>
              </div>
            ))
          }

          <div className={classnames('text-container-apl', classes.textContainer)}>
            {
                this.props.showBusinessNameFirst && this.props.showBusinessName &&
                <BusinessNameWithAds
                  showLabel={showAdsLabelWithBusinessName}
                  i18n={this.props.i18n}
                  businessName={previewTexts.businessName}
                  styleOverrides={styleOverrides}
                />
            }
            {
              this.props.showPreHeadlineDivider &&
              <div className={classnames('pre-headline-divider', classes.preHeadlineDivider)} />
            }
            {
                this.props.showShortHeadline &&
                (
                  <ShortHeadline
                    ref={this.multiLineTextRefs[0]}
                    showShortHeadlineMultiline={showShortHeadlineMultiline}
                    styleOverrides={styleOverrides}
                    shortHeadline={previewTexts.shortHeadline}
                  />
                )
              }
            {
                this.props.showLongHeadline &&
                (
                  <LongHeadline
                    ref={this.multiLineTextRefs[1]}
                    showMultipleLineLongHeadline={showMultipleLineLongHeadline}
                    styleOverrides={styleOverrides}
                    longHeadline={previewTexts.longHeadline}
                  />
                )
            }
            {
              this.props.showPostHeadlineDivider &&
              <div className={classnames('post-headline-divider', classes.postHeadlineDivider)} />
            }
            {
                this.props.showUrl &&
                (
                  <FinalUrl
                    ref={this.multiLineTextRefs[1]}
                    showUrlMultiline={showUrlMultiline}
                    styleOverrides={styleOverrides}
                    finalUrl={previewTexts.url}
                    displayUrl={previewTexts.displayUrl}
                  />
                )
            }
            {
                this.props.showDescription &&
                (
                  <Description
                    ref={this.multiLineTextRefs[2]}
                    showMultipleDescription={showMultipleDescription}
                    showAdsLabel={this.props.showAdsLabelInlineWithDescription}
                    adsLabelText={this.props.i18n.getString(_TL_('Ad'))}
                    styleOverrides={styleOverrides}
                    description={previewTexts.description}
                  />
                )
              }
            {
                this.props.showBusinessName && !this.props.showBusinessNameFirst &&
                <BusinessNameWithAds
                  showLabel={showAdsLabelWithBusinessName}
                  i18n={this.props.i18n}
                  businessName={previewTexts.businessName}
                  styleOverrides={styleOverrides}
                />
            }
            {
                this.props.showAction &&
                previewTexts.callToAction &&
                <CallToAction
                  actionText={previewTexts.callToAction.replace(/([A-Z])/g, ' $1')}
                  dismissText={dismissText}
                  styleOverrides={styleOverrides}
                />
            }
            {
                this.props.showPhoneAndAddressDecorator &&
                <ContactDecorator
                  data={decoratorsData.phoneAndAddress}
                  styleOverrides={styleOverrides}
                />
            }
          </div>
        </a>
        {
          showPreviewTypeString &&
          <div className={classnames('preview-string', classes.previewTypeString)} >
            {previewTypeStringKey}
          </div>
        }
        {
          showAdditionalDisclaimer &&
          <div className={classnames('additional-disclaimer-message', classes.additionalDisclaimer)}>
            {disclaimerMessage}
          </div>
        }
      </div>
    );
  }
}

UnstyledAdPlacementCardPreview.propTypes = {
  classes: PropTypes.shape({}).isRequired,
  ...AdPlacementCardPropTypes.propTypes,
};

UnstyledAdPlacementCardPreview.defaultProps = {
  ...AdPlacementCardPropTypes.defaultProps,
};

export const AdPlacementCardPreview = (props) => {
  const { styleOverrides } = props;
  // eslint-disable-next-line arrow-body-style
  const StyledComponent = useMemo(() => {
    return withI18nConsumer(withDefaultStyles(UnstyledAdPlacementCardPreview, getPlacementCardStyles({ styleOverrides })));
  }, [styleOverrides]);

  return (
    <StyledComponent
      {...props}
    />
  );
};

// just the prop for the Component combining HOCs
// see UnstyledAdPlacementCardPreview for actual component props
AdPlacementCardPreview.propTypes = {
  styleOverrides: PropTypes.shape({}).isRequired,
};
